import React, { useEffect, useState } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import CountrySelect from "../../common/contact/countryselector.component"
import StateSelect from "../../common/contact/stateselector.component"
import PostalCodeInput from "../../common/inputs/PostalCodeInputComponent"
import ContactAddressFormatter from "../../state/contact/AddressFormatter"
import { ContactAddressDto } from "../../state/contact/models"
import IntlProps from "../../state/helpers/IntlProps"

const messages = defineMessages({
    "street_placeholder": {
        id: "customer.modal.placeholder.street",
        defaultMessage: "Enter street",
    },
    "city_placeholder": {
        id: "customer.modal.placeholder.city",
        defaultMessage: "Enter city",
    },
    "postal_code_placeholder": {
        id: "customer.modal.placeholder.postal_code",
        defaultMessage: "Enter postal code",
    },
})

type OwnProps = {
    value: ContactAddressDto,
    onChange: (change) => void,
}

const AddressFormFields = (props: OwnProps & IntlProps) => {
    const [orderedFields, setOrderedFields] = useState<string[][]>([])
    const [fields, setFields] = useState({
        ...props.value
    })

    useEffect(() => {
        (async () => {
            const addressFormatter = new ContactAddressFormatter(fields)
            const orderedFields = await addressFormatter.getOrderedFields()
            setOrderedFields(orderedFields)
        })()
        return () => {
            setOrderedFields([])
        }
    }, [fields.countryCode])

    const customFieldValues = [props.value.countryCode, props.value.country, props.value.state, props.value.stateCode]
    useEffect(() => {
        setFields({
            ...props.value
        })
    }, [...customFieldValues])

    const onEventChange = (e) => {
        const {name, value} = e.target
        props.onChange({[name]: value})
    }

    const getFormField = (field, key) => {
        switch (field) {
        case "country":
            return (
                <div className='flex-fill p-2' key={key}>
                    <label htmlFor={"country"}>
                        <FormattedMessage
                            id='customer.modal.add_form.country_label'
                            defaultMessage='Country'
                        />
                    </label>
                    <CountrySelect
                        name='country'
                        countryCode={fields.countryCode}
                        onChange={(country, countryCode) => props.onChange({
                            country: country,
                            countryCode: countryCode,
                            state: "",
                            stateCode: "",
                        })
                        }
                    />
                </div>
            )
        case "city":
            return (
                <div className='flex-fill p-2' key={key}>
                    <label htmlFor={"city"}>
                        <FormattedMessage
                            id='customer.modal.add_form.city_label'
                            defaultMessage='City'
                        />
                    </label>

                    <input
                        type='text'
                        className='form-control'
                        id='city'
                        name='city'
                        value={fields.city}
                        placeholder={props.intl.formatMessage(messages["city_placeholder"])}
                        onChange={e => {
                            onEventChange(e)
                            setFields({
                                ...fields,
                                city: e.target.value,
                            })
                        }}
                    />
                </div>
            )
        case "zip":
            return (
                <div className='flex-fill flex-grow-0 p-2' key={key}>
                    <label htmlFor={"postalCode"}>
                        <FormattedMessage
                            id='customer.modal.add_form.postal_code_label'
                            defaultMessage='Postal Code'
                        />
                    </label>
                    <PostalCodeInput
                        type='text'
                        className='form-control'
                        id='postalCode'
                        name='postalCode'
                        postalCode={fields.postalCode}
                        placeholder={props.intl.formatMessage(messages["postal_code_placeholder"])}
                        countryCode={fields.countryCode}
                        onChange={e => {
                            onEventChange(e)
                            setFields({
                                ...fields,
                                postalCode: e.target.value,
                            })
                        }}
                    />
                </div>
            )
        case "province":
            return (
                <div className='flex-fill p-2' key={key}>
                    <label htmlFor={"state"}>
                        <FormattedMessage
                            id='customer.modal.add_form.state_label'
                            defaultMessage='State'
                        />
                    </label>
                    <StateSelect
                        name='state'
                        value={fields.stateCode}
                        countryCode={fields.countryCode}
                        onChange={(state, stateCode) => props.onChange({
                            state: state,
                            stateCode: stateCode
                        })}
                    />
                </div>
            )
        case "address1":
            return (
                <div className='flex-fill p-2' key={key}>
                    <label htmlFor={"street"}>
                        <FormattedMessage
                            id='customer.modal.add_form.street_label'
                            defaultMessage='Address'
                        />
                    </label>
                    <input
                        type='text'
                        className='form-control'
                        id='street'
                        name='street'
                        value={fields.street}
                        placeholder={props.intl.formatMessage(messages["street_placeholder"])}
                        onChange={e => {
                            onEventChange(e)
                            setFields({
                                ...fields,
                                street: e.target.value,
                            })
                        }}
                    />
                </div>
            )
        case "address2":
            return (
                <div className='flex-fill p-2' key={key}>
                    <label htmlFor='street2'>
                        <FormattedMessage
                            id='customer.modal.add_form.street2_label'
                            defaultMessage='Apartment, suite, etc.'
                        />
                    </label>

                    <input
                        type='text'
                        className='form-control'
                        id='street2'
                        name='street2'
                        value={fields.street2}
                        placeholder={props.intl.formatMessage(messages["street_placeholder"])}
                        onChange={e => {
                            onEventChange(e)
                            setFields({
                                ...fields,
                                street2: e.target.value,
                            })
                        }}
                    />
                </div>
            )
        }
    }

    return (
        <>
            {orderedFields.map((fieldArray, i) => {
                return <div className={"d-flex flex-fill"} key={i}>
                    {fieldArray.map((field, i) => {
                        return getFormField(field, i * 20)
                    })}
                </div>
            })}
        </>
    )
}

AddressFormFields.defaultProps = {
    address: {
        city: "",
        country: "",
        countryCode: "",
        postalCode: "",
        state: "",
        stateCode: "",
        street: "",
        street2: "",
    }
}

export default injectIntl(AddressFormFields)


// WEBPACK FOOTER //
// ./src/customer/components/AddressFormFieldsComponent.tsx